import { createContext, useContext } from 'react';
import type { SidebarDestination } from './sidebar-destinations';
import type { SidebarRef, SidebarSection } from './sidebar-layout.util';
import type { SidebarLayoutState } from './use-sidebar-layout.hook';

/** What a sidebar item's menu can do. */
export type SidebarMenuActionId =
  | 'rename'
  | 'share'
  | 'open-agent'
  | 'move-to-favorites'
  | 'move-to-pinned'
  | 'pin'
  | 'close'
  | 'unpin';

/** Where an item sits: a pin in a section, a chat's tab, or the page's tab. */
export type SidebarMenuPlace =
  | { kind: 'pin'; section: SidebarSection }
  | { kind: 'chat-tab' }
  | { kind: 'page-tab' };

/** The menu's own entries at its end, for the whole sidebar. */
export type SidebarMenuEndId =
  | 'add'
  | 'pages-as-rows'
  | 'pages-as-tiles'
  | 'reset';

export const MENU_LABELS: Record<
  SidebarMenuActionId | SidebarMenuEndId,
  string
> = {
  rename: 'Rename',
  share: 'Share…',
  'open-agent': 'Open agent',
  'move-to-favorites': 'Move to favorites',
  'move-to-pinned': 'Move to pinned',
  pin: 'Pin',
  close: 'Close',
  unpin: 'Unpin',
  add: 'Add to sidebar',
  'pages-as-rows': 'Show pages as rows',
  'pages-as-tiles': 'Show pages as tiles',
  reset: 'Reset sidebar',
};

/**
 * An item's menu, above the Add to sidebar, Show pages as rows (or tiles) and
 * Reset sidebar every menu ends with (Add to sidebar only while a page is
 * missing; the pages switch only while a page is pinned): a tile moves
 * to the rows, a row to the tiles, either unpins; a chat's tab pins or
 * closes; the page's tab pins. An agent's pin opens the agent too. A chat
 * whose title can be edited where it shows (`rename`) is renamed first; a
 * chat is then shared with the organization where sharing is on (`share`).
 */
export function menuActions(
  ref: SidebarRef,
  place: SidebarMenuPlace,
  { rename = false, share = false }: { rename?: boolean; share?: boolean } = {}
): SidebarMenuActionId[] {
  const first: SidebarMenuActionId[] =
    ref.kind === 'chat'
      ? [
          ...(rename ? (['rename'] as const) : []),
          ...(share ? (['share'] as const) : []),
        ]
      : [];
  if (place.kind === 'chat-tab') return [...first, 'pin', 'close'];
  if (place.kind === 'page-tab') return ['pin'];
  const move: SidebarMenuActionId =
    place.section === 'favorites' ? 'move-to-pinned' : 'move-to-favorites';
  return ref.kind === 'agent'
    ? ['open-agent', move, 'unpin']
    : [...first, move, 'unpin'];
}

/**
 * Opens the right-click menu of the sidebar item holding `target`, with its
 * corner at `point`: how the keyboard and a finger reach an item's menu,
 * since the menu opens on the item's contextmenu event.
 */
export function openItemMenu(
  target: EventTarget,
  point: { x: number; y: number }
): void {
  target.dispatchEvent(
    new MouseEvent('contextmenu', {
      bubbles: true,
      cancelable: true,
      clientX: point.x,
      clientY: point.y,
    })
  );
}

/**
 * The keys that open a focused item's menu: the menu key, and Shift+F10.
 * Browsers on Windows and Linux turn them into a contextmenu event on their
 * own; on a Mac neither does anything, so the item listens for them itself.
 */
export function isMenuKey(event: {
  key: string;
  shiftKey: boolean;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
}): boolean {
  if (event.key === 'ContextMenu') return true;
  return (
    event.key === 'F10' &&
    event.shiftKey &&
    !event.altKey &&
    !event.ctrlKey &&
    !event.metaKey
  );
}

/** What the sidebar's menus and drag and drop act on. */
export interface SidebarPins {
  layout: SidebarLayoutState;
  /** The pages the user can see that the layout leaves out, to add back. */
  missingPages: readonly SidebarDestination[];
  /** Hides one chat from the tabs until it moves again. */
  closeChat: (id: string) => void;
  /** Gives unpinned chats back their tabs, first. */
  reopenChats: (ids: readonly string[]) => void;
  /**
   * Remembers where focus goes when the item with `key` is about to move or
   * leave: the item where it lands, else its neighbour.
   */
  holdFocus: (key: string) => void;
  /** Moves focus there; false when nothing was held. */
  restoreFocus: () => boolean;
}

export const SidebarPinsContext = createContext<SidebarPins | null>(null);

/** The sidebar's pin actions, or null outside the tabbed sidebar. */
export function useSidebarPins(): SidebarPins | null {
  return useContext(SidebarPinsContext);
}
